import {
  CheckCircle,
  CircleOutlined,
  ExpandLess,
  ExpandMore,
  PhoneOutlined,
  PlaceOutlined,
} from '@mui/icons-material';
import {
  Card,
  CardActions,
  CardContent,
  Collapse,
  Divider,
  IconButton,
  Stack as MuiStack,
} from '@mui/material';
import { useState } from 'react';

import { CreditCardIcon } from '@/assets/icons';
import Action from '@/components/Action';
import Stack from '@/components/Stack';
import TextStack from '@/components/TextStack';
import { Subheading } from '@/components/Typography';
import Text from '@/elements/Text';
import { mapUrl } from '@/routes/Agency/Customer/AddressList';
import { theme } from '@/styles/colors';
import { GetCustomerAccountQuery } from '@/types/graphql';
import {
  formatCurrentPaymentMethod,
  getCurrentCreditCard,
} from '@/util/payments';

interface Props {
  account: GetCustomerAccountQuery['accounts'][0];
  selectedAccount?: string;
  handleSelect: (id: string) => void;
  phoneOnly: boolean;
}

const AccountCard = ({
  account,
  selectedAccount,
  handleSelect,
  phoneOnly,
}: Props) => {
  const isCardPM = getCurrentCreditCard(
    account.paymentMethodInfoType?.cards || [],
    account.currentPaymentMethod || ''
  );
  const isSelected = account.id === selectedAccount;
  const [expanded, setExpanded] = useState(false);

  return (
    <Card
      key={account.id}
      sx={{
        border: isSelected
          ? `1px solid ${theme.themeNew}`
          : `1px solid #D3D3D3`,
        borderRadius: '8px',
        background: isSelected ? `${theme.themeBackground} !important` : '',
        width: phoneOnly ? '100%' : '46%',
        cursor: 'pointer',
      }}
      variant="outlined"
      onClick={() => {
        handleSelect(account.id);
      }}
    >
      {phoneOnly && (
        <CardActions sx={{ padding: '10px' }}>
          <MuiStack direction={'row'} gap={1}>
            {isSelected ? (
              <CheckCircle
                fontSize="small"
                htmlColor={theme.themeNew}
                id="selected-icon"
                sx={{ top: '2px' }}
              />
            ) : (
              <CircleOutlined
                htmlColor="#D3D3D3"
                id="unselected-icon"
                sx={{ width: '20px', height: '20px', top: '2px' }}
              />
            )}
            <Subheading weight={'bold'}>{account.name}</Subheading>
          </MuiStack>

          <IconButton
            id="expand-icon"
            sx={{ marginLeft: 'auto', padding: '2px' }}
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </CardActions>
      )}

      <Collapse in={phoneOnly ? expanded : true}>
        <CardContent
          sx={
            phoneOnly
              ? {
                  borderTop: `1px solid ${
                    isSelected ? theme.themeNew : '#D3D3D3'
                  }`,
                }
              : { height: '310px' }
          }
        >
          <MuiStack gap={1.1}>
            {!phoneOnly && (
              <Stack justify={'apart'}>
                <Subheading weight={'bold'}>{account.name}</Subheading>
                {isSelected && (
                  <CheckCircle fontSize="small" htmlColor={theme.themeNew} />
                )}
              </Stack>
            )}
            <Stack align={'start'}>
              <PlaceOutlined fontSize="small" htmlColor={theme.themeNew} />
              <TextStack spacing="tight">
                <Action.Button
                  action={{
                    a11yLabel: `Open new tab to ${mapUrl(account)}`,
                    label: account.addressLine1!,
                    href: mapUrl(account),
                    external: true,
                  }}
                />
                {account.addressLine2 && (
                  <Text color={'lighter'}>{account.addressLine2}</Text>
                )}
                <Text color={'lighter'}>
                  {account.city}, {account.state} {account.zip}
                </Text>
              </TextStack>
            </Stack>
            <Stack>
              <PhoneOutlined fontSize="small" htmlColor={theme.themeNew} />
              <Text>{account.defaultContact?.user?.phoneNumber}</Text>
            </Stack>
            <Stack>
              <Text color={'lighter'}>Invoice Duration: </Text>
              <Text css={{ textTransform: 'capitalize' }}>
                {account.cutOff?.toLowerCase()}
              </Text>
            </Stack>
            <Divider
              sx={{
                pt: account.addressLine2 ? '6px' : '25px',
                mb: '8px',
              }}
            />

            {isCardPM ? (
              <>
                <Stack>
                  <CreditCardIcon />
                  <Text weight={'semibold'}>**** {isCardPM.card.last4} </Text>
                </Stack>
                <Stack>
                  <Text color={'lighter'}>Cardholder Name: </Text>
                  <Text weight={'semibold'}>
                    {isCardPM.card.holderName || 'N/A'}
                  </Text>
                </Stack>
                <Stack>
                  <Text color={'lighter'}>Expiration Date: </Text>
                  <Text weight={'semibold'}>
                    {isCardPM.card.expMonth}/{isCardPM.card.expYear?.slice(-2)}
                  </Text>
                </Stack>
              </>
            ) : (
              <Text weight={'semibold'}>
                {formatCurrentPaymentMethod({
                  currentPaymentMethod: account.currentPaymentMethod,
                  bankAccounts:
                    account.paymentMethodInfoType?.bankAccounts || [],
                  cards: account.paymentMethodInfoType?.cards || [],
                } as any)}
              </Text>
            )}
          </MuiStack>
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default AccountCard;
